<template>
  <focus-trap :escapeDeactivates="false" :clickDeactivates="false">
    <div class="print-options-popup" @keyup.esc.stop="cancel" tabindex="-1">
      <popup
        :buttons="['閉じる', '印刷']"
        @cancel="closeSelf"
        @decision="openPdf"
        @click-close-mark="closeSelf"
      >
        <template v-slot:content>
          <div class="content">
            <div class="title">{{ title }}</div>
            <div class="form">
              <div class="form-item" data-test="paper-size">
                <div class="label">用紙サイズ</div>
                <div class="input-field">
                  <div class="radio-buttons size">
                    <base-radio-button
                      v-for="option in radios.paperSize"
                      :input-styles="radioButtonStyle"
                      :key="'paper-size' + option.id"
                      :option="option"
                      v-model="printOptions.paperSize"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('receiptDate')"
                class="form-item"
                data-test="receipt-date"
              >
                <div class="label">発行日</div>
                <div class="input-field">
                  <v-date-picker
                    :popover="{ visibility: 'click' }"
                    v-model="printOptions.receiptDate"
                  >
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="v-date-picker"
                        :value="inputValue"
                        v-on="inputEvents"
                        readonly
                        ref="vDatePicker"
                      />
                    </template>
                  </v-date-picker>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('ownerName')"
                class="form-item"
                data-test="owner-name"
              >
                <div class="label">飼主名</div>
                <div class="input-field">
                  <base-text-box
                    :maxlength="31"
                    v-model="printOptions.ownerName"
                  />
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('proviso')"
                class="form-item"
                data-test="proviso"
              >
                <div class="label">但し書き</div>
                <div class="input-field">
                  <base-text-box
                    :maxlength="31"
                    v-model="printOptions.proviso"
                  />
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showClinicName')"
                class="form-item"
                data-test="clinic-name"
              >
                <div class="label">医院名</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.clinicName"
                      :input-styles="radioButtonStyle"
                      :key="'clinic-' + option.id"
                      :option="option"
                      v-model="printOptions.showClinicName"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showClinicTel')"
                class="form-item"
                data-test="clinic-tel"
              >
                <div class="label">医院電話番号</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.clinicTel"
                      :input-styles="radioButtonStyle"
                      :key="'clinic-' + option.id"
                      :option="option"
                      v-model="printOptions.showClinicTel"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showClinicEmail')"
                class="form-item"
                data-test="clinic-email"
              >
                <div class="label">医院メールアドレス</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.clinicEmail"
                      :input-styles="radioButtonStyle"
                      :key="'clinic-' + option.id"
                      :option="option"
                      v-model="printOptions.showClinicEmail"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showClinicAddress')"
                class="form-item"
                data-test="clinic-address"
              >
                <div class="label">医院住所</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.clinicAddress"
                      :input-styles="radioButtonStyle"
                      :key="'clinic-' + option.id"
                      :option="option"
                      v-model="printOptions.showClinicAddress"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="showFormItemInvoiceNumber"
                class="form-item"
                data-test="invoice-number"
              >
                <div class="label">登録番号</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.invoiceNumber"
                      :input-styles="radioButtonStyle"
                      :key="option.id"
                      :option="option"
                      v-model="printOptions.showInvoiceNumber"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showPrescription')"
                class="form-item"
                data-test="prescription-info"
              >
                <div class="label">処方の情報</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.prescriptionInfo"
                      :input-styles="radioButtonStyle"
                      :key="option.id"
                      :option="option"
                      v-model="printOptions.showPrescription"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showBarcode')"
                class="form-item"
                data-test="barcode"
              >
                <div class="label">自己負担額バーコード</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.barcode"
                      :input-styles="radioButtonStyle"
                      :key="option.id"
                      :option="option"
                      :value="printOptions.showBarcode"
                      @input="inputShowBarcode"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('showNextReservation')"
                class="form-item"
                data-test="next-reservation"
              >
                <div class="label">次回の予約</div>
                <div class="input-field">
                  <div class="radio-buttons">
                    <base-radio-button
                      v-for="option in radios.nextReservation"
                      :input-styles="radioButtonStyle"
                      :key="option.id"
                      :option="option"
                      v-model="printOptions.showNextReservation"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="isUsedPrintOption('isCopyRequired')"
                class="form-item"
                data-test="printing-copy"
              >
                <div class="label">控えの印刷</div>
                <div class="input-field">
                  <base-check-box
                    class="checkbox"
                    v-model="printOptions.isCopyRequired"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </popup>
      <announce-popup
        v-if="alertPopup.isOpened"
        v-bind="alertPopup"
        @close="alertPopup.close"
        >{{ alertPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="confirmationPopup.isOpened"
        v-bind="confirmationPopup"
        @cancel="confirmationPopup.cancel"
        @decision="confirmationPopup.decide"
        >{{ confirmationPopup.message }}</announce-popup
      >
    </div>
  </focus-trap>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseCheckBox from '@/components/parts/atoms/BaseCheckBox'
import BaseRadioButton from '@/components/parts/atoms/BaseRadioButton'
import BaseTextBox from '@/components/parts/atoms/BaseTextBox'
import CheckPopupInputDifference from '@/components/mixins/CheckPopupInputDifference'
import Popup from '@/components/popups/Popup'
import _ from 'lodash'
import moment from 'moment'
import { FocusTrap } from 'focus-trap-vue'
import { calcAge, makeBirthdayJp } from '@/utils/patient_info'
import { mapGetters } from 'vuex'
import { printEstimate } from '@/utils/print_estimate'
import { printExamination } from '@/utils/print_examination'
import { printMedicalPayment } from '@/utils/print_medical_payment'
import { printPrescription } from '@/utils/print_prescription'
import { printReceipt } from '@/utils/print_receipt'
import { printVaccinationCertificate } from '@/utils/print_vaccination_certificate'
import { waitThenMouseOver } from '@/utils/vDatePicker'

export default {
  name: 'PrintOptionsPopup',

  components: {
    AnnouncePopup,
    BaseCheckBox,
    BaseRadioButton,
    BaseTextBox,
    FocusTrap,
    Popup
  },

  mixins: [CheckPopupInputDifference],

  props: {
    dataType: { type: String, required: true },
    owner: { type: Object },
    patient: { type: Object },
    printData: { type: [Object, Array], required: true }, // 予防接種履歴のテーブルから予防接種証明書を印刷する場合は配列
    removingPrintOptionKeys: { type: Array, default: () => [] }
  },

  data() {
    return {
      alertPopup: {
        buttons: ['閉じる'],
        close: this.closeAlertPopup,
        isOpened: false,
        layerNumber: 2,
        message: '',
        title: '注意',
        type: 'alert'
      },
      confirmationPopup: {
        buttons: ['キャンセル', '閉じる'],
        cancel: this.closeConfirmationPopup,
        decide: this.emitClose,
        isOpened: false,
        layerNumber: 2,
        message: '',
        title: '確認',
        type: 'alert'
      },
      initialPrintOptions: {},
      printOptionKeysByType: {
        estimate: [
          'isCopyRequired',
          'paperSize',
          'showClinicAddress',
          'showClinicEmail',
          'showClinicName',
          'showClinicTel'
        ],
        examination: [
          'paperSize',
          'showClinicAddress',
          'showClinicEmail',
          'showClinicName',
          'showClinicTel'
        ],
        medicalPayment: [
          'paperSize',
          'showBarcode',
          'showClinicAddress',
          'showClinicEmail',
          'showClinicName',
          'showClinicTel',
          'showInvoiceNumber',
          'showPrescription',
          'showNextReservation'
        ],
        prescription: [
          'paperSize',
          'showClinicAddress',
          'showClinicEmail',
          'showClinicName',
          'showClinicTel'
        ],
        receipt: [
          'isCopyRequired',
          'ownerName',
          'paperSize',
          'proviso',
          'receiptDate',
          'showClinicAddress',
          'showClinicName',
          'showClinicTel',
          'showInvoiceNumber'
        ],
        vaccinationCertificate: [
          'paperSize',
          'showClinicAddress',
          'showClinicName',
          'showClinicTel'
        ]
      },
      printOptions: {},
      radioButtonStyle: { paddingLeft: '20px' },
      radios: {
        barcode: [
          { id: 'barcode-true', eachValue: true, labelName: '表示する' },
          { id: 'barcode-false', eachValue: false, labelName: '表示しない' }
        ],
        clinicAddress: [
          { id: 'address-true', eachValue: true, labelName: '表示する' },
          { id: 'address-false', eachValue: false, labelName: '表示しない' }
        ],
        clinicEmail: [
          { id: 'email-true', eachValue: true, labelName: '表示する' },
          { id: 'email-false', eachValue: false, labelName: '表示しない' }
        ],
        clinicName: [
          { id: 'name-true', eachValue: true, labelName: '表示する' },
          { id: 'name-false', eachValue: false, labelName: '表示しない' }
        ],
        clinicTel: [
          { id: 'tel-true', eachValue: true, labelName: '表示する' },
          { id: 'tel-false', eachValue: false, labelName: '表示しない' }
        ],
        invoiceNumber: [
          { id: 'invoice-true', eachValue: true, labelName: '表示する' },
          { id: 'invoice-false', eachValue: false, labelName: '表示しない' }
        ],
        prescriptionInfo: [
          { id: 'prescription-true', eachValue: true, labelName: '表示する' },
          {
            id: 'prescription-false',
            eachValue: false,
            labelName: '表示しない'
          }
        ],
        nextReservation: [
          { id: 'next-true', eachValue: true, labelName: '表示する' },
          { id: 'next-false', eachValue: false, labelName: '表示しない' }
        ],
        paperSize: [
          { id: 'A4', eachValue: 'A4', labelName: 'A4' },
          { id: 'B5', eachValue: 'B5', labelName: 'B5' },
          { id: 'A5', eachValue: 'A5', labelName: 'A5' }
        ]
      },
      showFormItemInvoiceNumber: false,
      timeoutIds: [],
      usingPrintOptionKeys: []
    }
  },

  computed: {
    ...mapGetters({
      clinic: 'clinic/getData',
      getMasterDatum: 'master/getDataById',
      getMedicalRecord: 'medicalRecords/getDataByOriginalId',
      getMedicine: 'medicines/getDataById',
      getMedicineByOriginalId: 'medicines/getDataByOriginalId',
      getPatient: 'patients/getDataById',
      getReservations: 'reservations/getOwnerReservations',
      printSetting: 'printSetting/getData'
    }),
    canShowBarcode() {
      if (this.dataType !== 'medicalPayment') return false
      return String(this.printData.burdenAmount).length <= 6
    },
    diffFlg() {
      return this.dataType === 'receipt'
        ? !_.isEqual(this.initialPrintOptions, this.printOptions)
        : false
    },
    title() {
      return this.dataType === 'estimate'
        ? '見積書の印刷'
        : this.dataType === 'examination'
        ? '検査結果の印刷'
        : this.dataType === 'medicalPayment'
        ? '診療明細の印刷'
        : this.dataType === 'prescription'
        ? '処方箋の印刷'
        : this.dataType === 'receipt'
        ? '領収書の印刷'
        : this.dataType === 'vaccinationCertificate'
        ? '予防接種証明書の印刷'
        : ''
    }
  },

  created() {
    // 使用/表示する印刷オプションを設定
    const printOptionKeys = this.printOptionKeysByType[this.dataType] ?? []
    this.usingPrintOptionKeys = printOptionKeys.filter(
      v => !this.removingPrintOptionKeys.includes(v)
    )
    const showInvoiceNumber = this.isUsedPrintOption('showInvoiceNumber')
      ? !!this.clinic.invoiceNumber
      : false
    if (showInvoiceNumber) this.showFormItemInvoiceNumber = true

    // 入力に使用する値を設定
    this.printOptions = {
      isCopyRequired: false,
      ownerName: this.owner
        ? `${this.owner.lastName} ${this.owner.firstName}`
        : '',
      paperSize: this.printSetting.defaultPaperSize,
      proviso: '診療代',
      receiptDate: new Date(),
      showBarcode: this.toShowBarcode(this.printSetting),
      showClinicAddress: !!this.printSetting.printClinicAddressFlg,
      showClinicEmail: !!this.printSetting.printClinicEmailFlg,
      showClinicName: !!this.printSetting.printClinicNameFlg,
      showClinicTel: !!this.printSetting.printClinicTelFlg,
      showInvoiceNumber,
      showPrescription: !!this.printSetting.prescriptionFlg,
      showNextReservation: false
    }
    if (this.dataType === 'receipt') {
      this.initialPrintOptions = _.cloneDeep(this.printOptions)
    }
  },

  mounted() {
    document.addEventListener('toggle-popover', this.onTogglePopover)
  },

  beforeDestroy() {
    document.removeEventListener('toggle-popover', this.onTogglePopover)
    this.timeoutIds.forEach(timeoutId => clearTimeout(timeoutId))
  },

  methods: {
    closeAlertPopup() {
      if (!this.canShowBarcode) {
        this.printOptions.showBarcode = false
      }
      this.alertPopup = {
        ...this.alertPopup,
        isOpened: false
      }
    },
    closeConfirmationPopup() {
      this.confirmationPopup = {
        ...this.confirmationPopup,
        isOpened: false
      }
    },
    closeSelf() {
      if (this.diffFlg) {
        this.confirmationPopup = {
          ...this.confirmationPopup,
          isOpened: true,
          message:
            '入力内容が変更されています。\nこのまま閉じてもよろしいですか？'
        }
      } else {
        this.emitClose()
      }
    },
    emitClose() {
      this.$emit('close')
    },
    filterPrintOptions(keys) {
      const filteredPrintOptions = keys.reduce((acc, key) => {
        acc[key] = this.printOptions[key]
        return acc
      }, {})
      return filteredPrintOptions
    },
    inputShowBarcode(show) {
      this.printOptions.showBarcode = show
      if (show && !this.canShowBarcode) {
        this.alertPopup = {
          ...this.alertPopup,
          isOpened: true,
          message: '自己負担額が7桁以上の場合バーコードは利用できません。'
        }
      }
    },
    isFutureReservation(reservation) {
      const currentDateTime = moment().format('YYYYMMDDHHmm')
      const reservationDateTime = `${reservation.date}${reservation.startTime}`
      return currentDateTime < reservationDateTime
    },
    isUsedPrintOption(key) {
      return this.usingPrintOptionKeys.includes(key)
    },
    makeNextReservationInfo() {
      if (!this.printOptions.showNextReservation) return null
      const medicalRecord = this.getMedicalRecord(
        this.printData.medicalRecordOriginalId
      )
      const ownerReservations = this.getReservations(this.owner.id)
      const ownerFutureReservations = ownerReservations.filter(
        reservation =>
          reservation.id !== medicalRecord.reservationId &&
          this.isFutureReservation(reservation)
      )
      if (!ownerFutureReservations.length) return null
      const latestReservation = ownerFutureReservations.reduce((a, b) =>
        a.date + a.startTime <= b.date + b.startTime ? a : b
      )
      const nextReservations = ownerFutureReservations.filter(
        v =>
          v.date === latestReservation.date &&
          v.startTime === latestReservation.startTime
      )
      const latestDateTime =
        latestReservation.date + latestReservation.startTime
      const dateText = `次回の予約：${this.toJpDatetime(latestDateTime)}~`
      const patientNames = nextReservations
        .map(reservation => {
          const patient = this.getPatient(reservation.patientId)
          return patient?.name ?? ''
        })
        .filter(v => !!v)
      const patientNamesText = Array.from(new Set(patientNames))
        .map(v => `${v} 様`)
        .join('\n')
      return {
        text: dateText,
        patientNames: patientNamesText
      }
    },
    makeSignatureItems() {
      const prefecture = this.getMasterDatum(
        'prefectures',
        this.clinic.prefectureId
      )
      const prefectureName = prefecture?.name ?? ''
      const clinic = { ...this.clinic, prefectureName }
      const { image: clinicImage, stamp: clinicStamp } = this.printSetting
      return { clinic, clinicImage, clinicStamp }
    },
    async onClick() {
      const timeoutId = await waitThenMouseOver(this.$refs.vDatePicker)
      this.timeoutIds.push(timeoutId)
    },
    async onTogglePopover() {
      const timeoutId = await waitThenMouseOver(
        this.$refs.vDatePicker,
        this.onClick
      )
      this.timeoutIds.push(timeoutId)
    },
    openPdf() {
      const signatureItems = this.makeSignatureItems()
      if (this.dataType === 'estimate') {
        this.openPdfEstimate(signatureItems)
      } else if (this.dataType === 'receipt') {
        this.openPdfReceipt(signatureItems)
      } else if (this.dataType === 'prescription') {
        this.openPdfPrescription(signatureItems)
      } else if (this.dataType === 'medicalPayment') {
        this.openPdfMedicalPayment(signatureItems)
      } else if (this.dataType === 'examination') {
        this.openPdfExamination(signatureItems)
      } else if (this.dataType === 'vaccinationCertificate') {
        this.openPdfVaccinationCertificate(signatureItems)
      }
    },
    openPdfEstimate(signatureItems) {
      const printOptions = this.filterPrintOptions(this.usingPrintOptionKeys)
      printEstimate({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.owner,
        patient: this.patient,
        estimate: this.printData
      })
    },
    openPdfExamination(signatureItems) {
      const printOptions = this.filterPrintOptions(this.usingPrintOptionKeys)
      const examinationDate = moment(this.printData.examination.date).format(
        'Y年M月D日'
      )
      printExamination({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.owner,
        patient: this.patient,
        examination: this.printData.examination,
        examinationDate
      })
    },
    openPdfMedicalPayment(signatureItems) {
      const nextReservationInfo = this.makeNextReservationInfo()
      const { showNextReservation, ...printOptions } = this.filterPrintOptions(
        this.usingPrintOptionKeys
      )
      printMedicalPayment({
        printOptions,
        barcodePrefix: this.printSetting.barcodePrefix,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        nextReservationInfo,
        printData: [
          {
            owner: this.owner,
            patient: this.patient,
            medicalPayment: this.printData
          }
        ]
      })
    },
    openPdfReceipt(signatureItems) {
      const printOptions = this.filterPrintOptions(this.usingPrintOptionKeys)
      printReceipt({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        payment: this.printData
      })
    },
    openPdfPrescription(signatureItems) {
      const printOptions = this.filterPrintOptions(this.usingPrintOptionKeys)
      const prescriptionItems = this.printData.prescriptionItems.map(v => {
        const medicine = v.medicineId
          ? this.getMedicine(v.medicineId)
          : this.getMedicineByOriginalId(v.medicineOriginalId)
        return { medicineName: medicine.name, prescription: v.prescription }
      })
      printPrescription({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.owner,
        patient: this.patient,
        treatmentDate: this.printData.treatmentDate,
        estimateDate: this.printData.estimateDate,
        prescriptionItems
      })
    },
    openPdfVaccinationCertificate(signatureItems) {
      const printOptions = this.filterPrintOptions(this.usingPrintOptionKeys)
      const ownerPrefecture = this.getMasterDatum(
        'prefectures',
        this.owner.prefectureId
      )
      const ownerPrefectureName = ownerPrefecture?.name ?? ''
      printVaccinationCertificate({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        clinicStamp: signatureItems.clinicStamp,
        owner: { ...this.owner, prefectureName: ownerPrefectureName },
        patient: {
          ...this.patient,
          displayBirthday: makeBirthdayJp(this.patient),
          age: calcAge(this.patient)
        },
        vaccineData: this.printData
      })
    },
    toJpDatetime(YYYYMMDDHHmm) {
      const output = 'Y年M月D日（dd）H時mm分'
      return moment(YYYYMMDDHHmm, 'YYYYMMDDHHmm').format(output)
    },
    toShowBarcode(printSetting) {
      return (
        this.dataType === 'medicalPayment' &&
        !!printSetting.barcodeFlg &&
        String(this.printData.burdenAmount).length <= 6
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.print-options-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    min-width: 460px;
    font-size: 15px;
    text-align: left;
    > .title {
      font-size: 16px;
      font-weight: bold;
    }
    > .form {
      margin-top: 25px;
      display: flex;
      flex-direction: column;
      row-gap: 10px;
      > .form-item {
        display: flex;
        height: 33px;
        > .label {
          width: 180px;
          display: flex;
          align-items: center;
        }
        > .input-field {
          flex: 1;
          display: flex;
          align-items: center;
          > .radio-buttons {
            display: flex;
            column-gap: 20px;
            &.size {
              column-gap: 10px;
            }
          }
        }
        ::v-deep {
          .v-date-picker {
            box-sizing: border-box;
            text-align: right;
            padding-right: 12px;
            width: 140px;
            height: 33px;
            border-radius: 5px;
            border: solid 2px #{$light-grey};
            text-indent: 5px;
            font-size: 13px;
            cursor: pointer;
            background-image: url('../../assets/images/calendar.png');
            background-size: 18px 18px;
            background-repeat: no-repeat;
            background-position: 5px center;
          }
        }
      }
    }
  }
}
</style>
